import React, { useEffect, useMemo, useState } from "react";
import { fmtDate, fmtDateTime } from "@/lib/dates";
import { api, apiErr } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { uploadFile } from "@/lib/api";
import { Check, X, GraduationCap, Plus, Send, Trash2, Loader2, Sparkles, Pencil, Archive, ArchiveRestore } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import AdvisorAdmin from "@/pages/dashboard/AdvisorAdmin";
import QuizReview from "@/pages/dashboard/QuizReview";
import CareHomesAdmin from "@/pages/dashboard/CareHomesAdmin";
import SiteImagesAdmin from "@/pages/dashboard/SiteImagesAdmin";
import SiteTextAdmin from "@/pages/dashboard/SiteTextAdmin";
import TestimonialsAdmin from "@/pages/dashboard/TestimonialsAdmin";
import FaqAdmin from "@/pages/dashboard/FaqAdmin";
import SelfChecksAdmin from "@/pages/dashboard/SelfChecksAdmin";

export default function Admin() {
  return (
    <div>
      <PageHeader title="Manage platform" subtitle="Care homes, applications, leads, live sessions, content and campaigns." />
      <Tabs defaultValue="homes">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="homes" data-testid="tab-homes">Care homes</TabsTrigger>
          <TabsTrigger value="tt" data-testid="tab-tt">Train the Trainer</TabsTrigger>
          <TabsTrigger value="champions" data-testid="tab-champions">Oral Champions</TabsTrigger>
          <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
          <TabsTrigger value="selfchecks" data-testid="tab-selfchecks">Self-checks</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="tab-sessions">Live sessions</TabsTrigger>
          <TabsTrigger value="courses" data-testid="tab-courses">Courses</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="tab-campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="advisor" data-testid="tab-advisor">Ask Jane</TabsTrigger>
          <TabsTrigger value="images" data-testid="tab-images">Website</TabsTrigger>
        </TabsList>
        <TabsContent value="homes"><CareHomesAdmin /></TabsContent>
        <TabsContent value="tt"><TTApplications /></TabsContent>
        <TabsContent value="champions"><OralChampions /></TabsContent>
        <TabsContent value="leads"><Leads /></TabsContent>
        <TabsContent value="selfchecks"><SelfChecksAdmin /></TabsContent>
        <TabsContent value="sessions"><SessionsAdmin /></TabsContent>
        <TabsContent value="courses"><CoursesAdmin /></TabsContent>
        <TabsContent value="products"><ProductsAdmin /></TabsContent>
        <TabsContent value="campaigns"><Campaigns /></TabsContent>
        <TabsContent value="advisor"><AdvisorAdmin /></TabsContent>
        <TabsContent value="images">
          <Tabs defaultValue="photos">
            <TabsList className="mb-5"><TabsTrigger value="photos" data-testid="tab-web-photos">Photos</TabsTrigger><TabsTrigger value="text" data-testid="tab-web-text">Text</TabsTrigger><TabsTrigger value="testimonials" data-testid="tab-web-testimonials">Testimonials</TabsTrigger><TabsTrigger value="faqs" data-testid="tab-web-faqs">FAQs</TabsTrigger></TabsList>
            <TabsContent value="photos"><SiteImagesAdmin /></TabsContent>
            <TabsContent value="text"><SiteTextAdmin /></TabsContent>
            <TabsContent value="testimonials"><TestimonialsAdmin /></TabsContent>
            <TabsContent value="faqs"><FaqAdmin /></TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TTApplications() {
  const [apps, setApps] = useState([]);
  const load = () => api.get("/train-the-trainer/applications").then((r) => setApps(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const decide = async (id, status) => {
    try { await api.post(`/train-the-trainer/${id}/decision`, { status }); toast.success(`Application ${status}`); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };
  const graduate = async (id) => {
    try { await api.post("/train-the-trainer/graduate", { app_id: id }); toast.success("Graduated & added to trainer roster"); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete the application from ${a.name}? This cannot be undone.`)) return;
    try { await api.delete(`/train-the-trainer/${a.id}`); toast.success("Application deleted"); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  return (
    <div className="bg-cream-card border border-cream-deep rounded-xl overflow-hidden">
      <Table>
        <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Applicant</TableHead><TableHead>Care home</TableHead><TableHead>Status</TableHead><TableHead>Paid</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
        <TableBody>
          {apps.map((a, i) => (
            <TableRow key={a.id} data-testid={`tt-app-${i}`} className={i % 2 ? "bg-cream/50" : ""}>
              <TableCell><div className="font-medium">{a.name}</div><div className="text-xs text-muted-foreground">{a.email}</div></TableCell>
              <TableCell>{a.care_home}</TableCell>
              <TableCell><Badge variant="outline" className="capitalize">{a.status}</Badge></TableCell>
              <TableCell>{a.paid ? <Check className="h-4 w-4 text-rag-green" /> : <X className="h-4 w-4 text-muted-foreground" />}</TableCell>
              <TableCell className="text-right space-x-2">
                {a.status === "pending" && <>
                  <Button size="sm" data-testid={`tt-approve-${i}`} onClick={() => decide(a.id, "approved")} className="rounded-full bg-rag-green text-white h-8">Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => decide(a.id, "rejected")} className="rounded-full h-8">Reject</Button>
                </>}
                {a.paid && a.status !== "graduated" && <Button size="sm" data-testid={`tt-graduate-${i}`} onClick={() => graduate(a.id)} className="rounded-full bg-brand text-white h-8"><GraduationCap className="h-4 w-4 mr-1" />Graduate</Button>}
                {a.status === "graduated" && <Badge className="bg-rag-green-bg text-rag-green">On roster</Badge>}
                <Button size="sm" variant="ghost" data-testid={`tt-delete-${i}`} onClick={() => remove(a)} className="h-8 w-8 p-0 text-muted-foreground hover:text-coral" title="Delete application"><Trash2 className="h-4 w-4" /></Button>
              </TableCell>
            </TableRow>
          ))}
          {apps.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">No applications yet.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}

function OralChampions() {
  const [apps, setApps] = useState([]);
  const [webinar, setWebinar] = useState({ zoom_link: "", info_url: "" });
  const [savingW, setSavingW] = useState(false);

  const load = () => api.get("/oral-champion/applications").then((r) => setApps(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/oral-champion/webinar").then((r) => setWebinar(r.data)).catch(() => {}); }, []);

  const saveWebinar = async () => {
    setSavingW(true);
    try { await api.post("/admin/oral-champion/webinar", webinar); toast.success("Webinar details saved"); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSavingW(false); }
  };

  const markAttended = async (id) => {
    try { await api.post(`/oral-champion/applications/${id}/attended`); toast.success("Marked attended — certificate issued"); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete the registration from ${a.registrant_name}? This cannot be undone.`)) return;
    try { await api.delete(`/oral-champion/applications/${a.id}`); toast.success("Registration deleted"); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <div className="bg-cream-card border border-cream-deep rounded-xl p-5">
        <h3 className="font-heading font-bold text-brand mb-1">Webinar link</h3>
        <p className="text-sm text-muted-foreground mb-3">Emailed to every registrant on booking and in reminders. Update if your recurring Zoom link changes.</p>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Zoom webinar link</Label><Input data-testid="oc-zoom-link" placeholder="https://zoom.us/j/…" value={webinar.zoom_link || ""} onChange={(e) => setWebinar({ ...webinar, zoom_link: e.target.value })} /></div>
          <div><Label>Info page URL</Label><Input data-testid="oc-info-url" placeholder="https://www.kohc.co.uk/oral-care-champions" value={webinar.info_url || ""} onChange={(e) => setWebinar({ ...webinar, info_url: e.target.value })} /></div>
        </div>
        <Button data-testid="oc-webinar-save" onClick={saveWebinar} disabled={savingW} className="mt-3 rounded-full bg-brand text-white h-9">{savingW ? "Saving…" : "Save webinar details"}</Button>
      </div>

      <div className="bg-cream-card border border-cream-deep rounded-xl overflow-hidden">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Registrant</TableHead><TableHead>Care home</TableHead><TableHead>Session</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Attendance</TableHead></TableRow></TableHeader>
          <TableBody>
            {apps.map((a, i) => (
              <TableRow key={a.id} data-testid={`oc-app-${i}`} className={i % 2 ? "bg-cream/50" : ""}>
                <TableCell><div className="font-medium">{a.registrant_name}</div><div className="text-xs text-muted-foreground">{a.email}{a.job_title ? ` · ${a.job_title}` : ""}</div></TableCell>
                <TableCell>{a.care_home_name}</TableCell>
                <TableCell className="text-sm">{a.session_date || "—"}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="capitalize">{a.status}</Badge>
                  {!a.included && <Badge className="ml-1 bg-coral/10 text-coral">£42.50</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  {a.certificate_id && <Badge className="bg-rag-green-bg text-rag-green">Attended · certified</Badge>}
                  {!a.certificate_id && a.status === "confirmed" && <Button size="sm" data-testid={`oc-attended-${i}`} onClick={() => markAttended(a.id)} className="rounded-full bg-rag-green text-white h-8"><Check className="h-4 w-4 mr-1" />Mark attended</Button>}
                  {!a.certificate_id && a.status !== "confirmed" && <span className="text-xs text-muted-foreground">Awaiting payment</span>}
                  <Button size="sm" variant="ghost" data-testid={`oc-delete-${i}`} onClick={() => remove(a)} className="ml-2 h-8 w-8 p-0 text-muted-foreground hover:text-coral" title="Delete registration"><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {apps.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">No Oral Champion registrations yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Leads() {
  const [leads, setLeads] = useState([]);
  useEffect(() => { api.get("/leads").then((r) => setLeads(r.data)).catch(() => {}); }, []);
  return (
    <div className="bg-cream-card border border-cream-deep rounded-xl overflow-hidden">
      <Table>
        <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Care home</TableHead><TableHead>Types</TableHead><TableHead>Source</TableHead></TableRow></TableHeader>
        <TableBody>
          {leads.map((l, i) => (
            <TableRow key={l.id} data-testid={`lead-row-${i}`} className={i % 2 ? "bg-cream/50" : ""}>
              <TableCell className="font-medium">{l.name}</TableCell><TableCell className="text-muted-foreground">{l.email}</TableCell>
              <TableCell>{l.care_home_name}</TableCell>
              <TableCell><div className="flex flex-wrap gap-1">{(l.care_home_types || []).map((t) => <Badge key={t} variant="outline" className="text-xs">{t}</Badge>)}</div></TableCell>
              <TableCell><Badge>{l.source}</Badge></TableCell>
            </TableRow>
          ))}
          {leads.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">No leads captured yet.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}

function SessionsAdmin() {
  const [sessions, setSessions] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "Oral Champion Course — Live", trainer_id: "", scheduled_at: "", capacity: 20, zoom_link: "https://us02web.zoom.us/j/5772568992" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/sessions").then((r) => setSessions(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/trainers").then((r) => setTrainers(r.data)).catch(() => {}); }, []);

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/sessions", { ...form, capacity: Number(form.capacity), scheduled_at: new Date(form.scheduled_at).toISOString(), course_type: "Oral Champion", duration_min: 180 });
      toast.success("Session scheduled"); setOpen(false); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-session-btn" className="rounded-full bg-brand hover:bg-brand-hover text-white"><Plus className="h-4 w-4 mr-2" />Schedule session</Button></DialogTrigger>
          <DialogContent data-testid="session-dialog">
            <DialogHeader><DialogTitle>Schedule a live session</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div><Label>Title</Label><Input data-testid="session-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><Label>Trainer</Label>
                <Select value={form.trainer_id} onValueChange={(v) => setForm({ ...form, trainer_id: v })}>
                  <SelectTrigger data-testid="session-trainer"><SelectValue placeholder="Assign a trainer" /></SelectTrigger>
                  <SelectContent>{trainers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date & time</Label><Input data-testid="session-datetime" type="datetime-local" required value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} /></div>
                <div><Label>Capacity</Label><Input data-testid="session-capacity" type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} /></div>
              </div>
              <div><Label>Zoom link</Label><Input data-testid="session-zoom" placeholder="https://zoom.us/j/..." value={form.zoom_link} onChange={(e) => setForm({ ...form, zoom_link: e.target.value })} /></div>
              <DialogFooter><Button data-testid="session-create-btn" type="submit" disabled={busy} className="rounded-full bg-coral text-white">Schedule</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-cream-card border border-cream-deep rounded-xl overflow-hidden">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Session</TableHead><TableHead>Trainer</TableHead><TableHead>When</TableHead><TableHead className="text-center">Booked</TableHead></TableRow></TableHeader>
          <TableBody>
            {sessions.map((s, i) => (
              <TableRow key={s.id} data-testid={`admin-session-${i}`} className={i % 2 ? "bg-cream/50" : ""}>
                <TableCell className="font-medium">{s.title}</TableCell><TableCell>{s.trainer_name}</TableCell>
                <TableCell>{fmtDateTime(s.scheduled_at)}</TableCell>
                <TableCell className="text-center">{s.booked_count}/{s.capacity}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

const SEGMENTS = ["all", "Nursing", "Residential", "Dementia", "Learning disabilities", "Hospice", "Hospital", "Community care", "Assisted living", "Other"];

const uid = () => Math.random().toString(36).slice(2);
function emptyLesson() {
  return { _k: uid(), title: "", duration_min: 10, video_url: "", captions_url: "", content: "", transcript: "", reflection_prompt: "", quiz: [] };
}
function emptyQuestion() {
  return { _k: uid(), prompt: "", options: ["", "", "", ""], correct_index: 0 };
}

const submitLabel = (busy, editingId) => { if (busy) return "Saving…"; return editingId ? "Save changes" : "Create course"; };


function CoursesAdmin() {
  const [courses, setCourses] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", category: "Core Training", cpd_hours: 1, thumbnail: "" });
  const [lessons, setLessons] = useState([emptyLesson()]);
  const [uploading, setUploading] = useState("");
  const [genIdx, setGenIdx] = useState(-1);

  const [showArchived, setShowArchived] = useState(false);
  const archivedCount = useMemo(() => courses.filter((c) => c.published === false).length, [courses]);
  const visibleCourses = useMemo(() => courses.filter((c) => showArchived || c.published !== false), [courses, showArchived]);
  const load = () => api.get("/courses", { params: { include_archived: true } }).then((r) => setCourses(r.data)).catch(() => {});
  const toggleArchive = async (c) => {
    const archived = c.published === false;
    try { await api.post(`/courses/${c.id}/${archived ? "restore" : "archive"}`); toast.success(archived ? "Course restored — visible to staff again" : "Course archived — hidden from staff"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const removeCourse = async (c) => {
    if (!window.confirm(`Delete "${c.title}"? Staff enrolments for this course will be removed (certificates are kept).`)) return;
    try { await api.delete(`/courses/${c.id}`); toast.success("Course deleted"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm({ title: "", description: "", category: "Core Training", cpd_hours: 1, thumbnail: "" });
    setLessons([emptyLesson()]);
  };

  const openNew = () => { resetForm(); setOpen(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({ title: c.title, description: c.description, category: c.category, cpd_hours: c.cpd_hours, thumbnail: c.thumbnail || "" });
    setLessons((c.lessons || []).map((l) => ({
      ...emptyLesson(), ...l,
      quiz: (l.quiz || []).map((q) => ({ _k: uid(), prompt: q.prompt, options: [...(q.options || []), "", "", "", ""].slice(0, 4), correct_index: q.correct_index || 0 })),
    })));
    setOpen(true);
  };

  const patchLesson = (i, patch) => setLessons((ls) => ls.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const doUpload = async (file, key, idx) => {
    if (!file) return;
    setUploading(key + idx);
    try {
      const { url } = await uploadFile(file);
      if (key === "thumb") setForm((f) => ({ ...f, thumbnail: url }));
      else if (key === "captions") patchLesson(idx, { captions_url: url });
      else patchLesson(idx, { video_url: url });
      toast.success("Uploaded");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setUploading(""); }
  };

  const generateQuiz = async (idx) => {
    const l = lessons[idx];
    if (!l.transcript?.trim()) { toast.error("Add a transcript first, then generate the quiz."); return; }
    setGenIdx(idx);
    try {
      const { data } = await api.post("/courses/generate-quiz", { transcript: l.transcript });
      const quiz = (data.questions || []).map((q) => ({ _k: uid(), prompt: q.prompt, options: [...q.options, "", "", "", ""].slice(0, 4), correct_index: q.correct_index || 0 }));
      patchLesson(idx, { quiz });
      toast.success(`Generated ${quiz.length} questions — review and edit as needed.`);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setGenIdx(-1); }
  };

  const addQuestion = (i) => patchLesson(i, { quiz: [...(lessons[i].quiz || []), emptyQuestion()] });
  const removeQuestion = (i, qi) => patchLesson(i, { quiz: lessons[i].quiz.filter((_, k) => k !== qi) });
  const patchQuestion = (i, qi, patch) => patchLesson(i, { quiz: lessons[i].quiz.map((q, k) => (k === qi ? { ...q, ...patch } : q)) });
  const patchOption = (i, qi, oi, val) => {
    const q = lessons[i].quiz[qi];
    const options = q.options.map((o, k) => (k === oi ? val : o));
    patchQuestion(i, qi, { options });
  };

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = {
        ...form, cpd_hours: Number(form.cpd_hours), published: true,
        lessons: lessons.filter((l) => l.title).map((l) => ({
          ...l,
          quiz: (l.quiz || []).filter((q) => q.prompt.trim() && q.options.filter((o) => o.trim()).length >= 2)
            .map((q) => ({ prompt: q.prompt.trim(), options: q.options.filter((o) => o.trim()), correct_index: Math.min(q.correct_index, q.options.filter((o) => o.trim()).length - 1) })),
        })),
      };
      if (editingId) { await api.put(`/courses/${editingId}`, payload); toast.success("Course updated"); }
      else { await api.post("/courses", payload); toast.success("Course created"); }
      setOpen(false); resetForm(); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-muted-foreground">Upload videos, add a transcript, and build a quiz (AI-assisted) for each module.</p>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
          <Button data-testid="new-course-btn" onClick={openNew} className="rounded-full bg-brand hover:bg-brand-hover text-white"><Plus className="h-4 w-4 mr-2" />New course</Button>
          <DialogContent data-testid="course-dialog" className="max-w-3xl max-h-[88vh] overflow-auto">
            <DialogHeader><DialogTitle>{editingId ? "Edit course" : "Create a course"}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label>Title</Label><Input data-testid="course-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div><Label>Category</Label><Input data-testid="course-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
              </div>
              <div><Label>Description</Label><Input data-testid="course-desc" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid md:grid-cols-2 gap-3 items-end">
                <div><Label>CPD hours</Label><Input data-testid="course-cpd" type="number" step="0.5" value={form.cpd_hours} onChange={(e) => setForm({ ...form, cpd_hours: e.target.value })} /></div>
                <div>
                  <Label>Thumbnail</Label>
                  <Input data-testid="course-thumb" type="file" accept="image/*" onChange={(e) => doUpload(e.target.files[0], "thumb", 0)} />
                  {uploading === "thumb0" && <span className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><Loader2 className="h-3 w-3 animate-spin" />Uploading…</span>}
                  {form.thumbnail && <span className="text-xs text-rag-green">✓ uploaded</span>}
                </div>
              </div>
              <div className="border-t border-cream-deep pt-4">
                <div className="flex items-center justify-between mb-2"><Label>Modules</Label>
                  <Button type="button" size="sm" variant="outline" data-testid="add-lesson-btn" onClick={() => setLessons([...lessons, emptyLesson()])} className="rounded-full h-8"><Plus className="h-3 w-3 mr-1" />Add module</Button>
                </div>
                <div className="space-y-4">
                  {lessons.map((l, i) => (
                    <div key={l._k || i} className="bg-cream rounded-lg p-3 space-y-2 border border-cream-deep" data-testid={`lesson-editor-${i}`}>
                      <div className="grid grid-cols-3 gap-2">
                        <Input className="col-span-2" placeholder="Module title" data-testid={`lesson-title-${i}`} value={l.title} onChange={(e) => patchLesson(i, { title: e.target.value })} />
                        <Input type="number" placeholder="min" value={l.duration_min} onChange={(e) => patchLesson(i, { duration_min: Number(e.target.value) })} />
                      </div>
                      <Input placeholder="Short lesson notes / summary (optional)" value={l.content} onChange={(e) => patchLesson(i, { content: e.target.value })} />
                      <div className="flex items-center gap-2">
                        <Input type="file" accept="video/*" data-testid={`lesson-video-${i}`} onChange={(e) => doUpload(e.target.files[0], "video", i)} />
                        {uploading === "video" + i && <Loader2 className="h-4 w-4 animate-spin text-brand" />}
                        {l.video_url && <span className="text-xs text-rag-green whitespace-nowrap">✓ video</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Input type="file" accept=".srt,.vtt" data-testid={`lesson-captions-${i}`} title="Captions (.srt from HeyGen or .vtt)" onChange={(e) => doUpload(e.target.files[0], "captions", i)} />
                        {uploading === "captions" + i && <Loader2 className="h-4 w-4 animate-spin text-brand" />}
                        {l.captions_url ? <button type="button" data-testid={`lesson-captions-clear-${i}`} onClick={() => patchLesson(i, { captions_url: "" })} className="text-xs text-rag-green whitespace-nowrap hover:underline">✓ captions · remove</button>
                          : <span className="text-xs text-muted-foreground whitespace-nowrap">Captions (.srt / .vtt)</span>}
                      </div>
                      <Textarea rows={3} placeholder="Transcript (used for the quiz and shown to staff)" data-testid={`lesson-transcript-${i}`} value={l.transcript} onChange={(e) => patchLesson(i, { transcript: e.target.value })} />
                      <Input placeholder="Reflection question (optional, free text, not scored)" data-testid={`lesson-reflection-${i}`} value={l.reflection_prompt} onChange={(e) => patchLesson(i, { reflection_prompt: e.target.value })} />

                      <div className="border-t border-cream-deep pt-2">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-brand">Quiz ({(l.quiz || []).length})</span>
                          <div className="flex gap-2">
                            <Button type="button" size="sm" variant="outline" data-testid={`gen-quiz-${i}`} disabled={genIdx === i} onClick={() => generateQuiz(i)} className="rounded-full h-7 text-xs">
                              {genIdx === i ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}Generate with AI
                            </Button>
                            <Button type="button" size="sm" variant="outline" data-testid={`add-question-${i}`} onClick={() => addQuestion(i)} className="rounded-full h-7 text-xs"><Plus className="h-3 w-3 mr-1" />Question</Button>
                          </div>
                        </div>
                        <div className="space-y-3">
                          {(l.quiz || []).map((q, qi) => (
                            <div key={q._k || qi} className="bg-cream-card rounded-lg p-2 border border-cream-deep" data-testid={`question-${i}-${qi}`}>
                              <div className="flex items-start gap-2">
                                <Input className="flex-1" placeholder={`Question ${qi + 1}`} value={q.prompt} onChange={(e) => patchQuestion(i, qi, { prompt: e.target.value })} />
                                <button type="button" onClick={() => removeQuestion(i, qi)} className="text-muted-foreground hover:text-red-600 mt-2"><Trash2 className="h-4 w-4" /></button>
                              </div>
                              <div className="grid grid-cols-2 gap-2 mt-2">
                                {q.options.map((opt, oi) => (
                                  <label key={`${q._k || qi}-${oi}`} className="flex items-center gap-1.5">
                                    <input type="radio" name={`correct-${i}-${qi}`} checked={q.correct_index === oi} onChange={() => patchQuestion(i, qi, { correct_index: oi })} title="Correct answer" />
                                    <Input className="h-8 text-sm" placeholder={`Option ${oi + 1}`} value={opt} onChange={(e) => patchOption(i, qi, oi, e.target.value)} />
                                  </label>
                                ))}
                              </div>
                              <p className="text-[10px] text-muted-foreground mt-1">Select the radio next to the correct answer.</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter><Button data-testid="course-create-submit" type="submit" disabled={busy} className="rounded-full bg-coral text-white">{submitLabel(busy, editingId)}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      {archivedCount > 0 && (
        <label className="flex items-center gap-2 text-sm text-muted-foreground mb-3"><Switch data-testid="show-archived-toggle" checked={showArchived} onCheckedChange={setShowArchived} />Show archived courses ({archivedCount})</label>
      )}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {visibleCourses.map((c) => (
          <div key={c.id} data-testid={`admin-course-${c.id}`} className={`bg-cream-card border rounded-xl overflow-hidden ${c.published === false ? "border-dashed border-cream-deep opacity-75" : "border-cream-deep"}`}>
            {c.thumbnail && <img src={c.thumbnail} alt={c.title} className="h-32 w-full object-cover" />}
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex gap-1 flex-wrap"><Badge variant="outline">{c.category}</Badge>{c.published === false && <Badge data-testid={`archived-badge-${c.id}`} className="bg-cream-deep text-brand">Archived</Badge>}</div>
                <div className="flex gap-1">
                  <Button type="button" size="sm" variant="ghost" data-testid={`archive-course-${c.id}`} onClick={() => toggleArchive(c)} className="h-7 px-2 text-xs">{c.published === false ? <><ArchiveRestore className="h-3 w-3 mr-1" />Restore</> : <><Archive className="h-3 w-3 mr-1" />Archive</>}</Button>
                  <Button type="button" size="sm" variant="ghost" data-testid={`edit-course-${c.id}`} onClick={() => openEdit(c)} className="h-7 px-2 text-xs"><Pencil className="h-3 w-3 mr-1" />Edit</Button>
                  <Button type="button" size="sm" variant="ghost" data-testid={`delete-course-${c.id}`} onClick={() => removeCourse(c)} className="h-7 px-2 text-xs text-rag-red"><Trash2 className="h-3 w-3 mr-1" />Delete</Button>
                </div>
              </div>
              <h4 className="font-heading font-bold text-brand mt-1">{c.title}</h4>
              <p className="text-xs text-muted-foreground mt-1">{c.lessons?.length || 0} modules · {c.cpd_hours} CPD hrs · {(c.lessons || []).reduce((n, l) => n + (l.quiz?.length || 0), 0)} quiz questions</p>
              <div className="mt-3"><QuizReview course={c} onSaved={load} /></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Campaigns() {
  const [segment, setSegment] = useState("all");
  const [audience, setAudience] = useState("leads");
  const [counts, setCounts] = useState({ leads: 0, homes: 0 });
  const [past, setPast] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadPast = () => api.get("/campaigns").then((r) => setPast(r.data)).catch(() => {});
  useEffect(() => { loadPast(); }, []);
  useEffect(() => { api.get(`/campaigns/audience?segment=${encodeURIComponent(segment)}`).then((r) => setCounts(r.data)).catch(() => {}); }, [segment]);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/campaigns/reactivate", { segment, audience });
      toast.success(`Campaign sent to ${data.recipients} recipient${data.recipients !== 1 ? "s" : ""}`);
      loadPast();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const recipientCount = audience === "leads" ? counts.leads : counts.homes;
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-cream-card border border-cream-deep rounded-xl p-6">
        <h3 className="font-heading font-bold text-brand">Reactivate the 500</h3>
        <p className="text-sm text-muted-foreground mt-1">Invite past-trained homes and leads onto a free trial, segmented by care home type.</p>
        <div className="space-y-4 mt-5">
          <div>
            <Label>Audience</Label>
            <Select value={audience} onValueChange={setAudience}>
              <SelectTrigger data-testid="campaign-audience"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="leads">Leads (checklist downloads)</SelectItem><SelectItem value="homes">Care home managers</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label>Care home type segment</Label>
            <Select value={segment} onValueChange={setSegment}>
              <SelectTrigger data-testid="campaign-segment"><SelectValue /></SelectTrigger>
              <SelectContent>{SEGMENTS.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All segments" : s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="rounded-lg bg-brand-light p-4 text-sm text-brand" data-testid="campaign-count">
            <span className="font-bold text-lg">{recipientCount}</span> recipient{recipientCount !== 1 ? "s" : ""} match this segment
          </div>
          <Button data-testid="campaign-send-btn" onClick={send} disabled={busy || recipientCount === 0} className="w-full rounded-full bg-coral hover:bg-coral-hover text-white">
            <Send className="h-4 w-4 mr-2" />{busy ? "Sending…" : "Send reactivation campaign"}
          </Button>
        </div>
      </div>
      <div className="bg-cream-card border border-cream-deep rounded-xl p-6">
        <h3 className="font-heading font-bold text-brand mb-3">Recent campaigns</h3>
        <div className="space-y-2">
          {past.map((c, i) => (
            <div key={c.id} data-testid={`campaign-row-${i}`} className="flex items-center justify-between text-sm py-2 border-b border-cream-deep last:border-0">
              <div><span className="font-medium capitalize">{c.audience}</span> · {c.segment === "all" ? "All" : c.segment}</div>
              <div className="text-muted-foreground">{c.delivered}/{c.recipients} sent · {fmtDate(c.created_at)}</div>
            </div>
          ))}
          {past.length === 0 && <p className="text-sm text-muted-foreground">No campaigns sent yet.</p>}
        </div>
      </div>
    </div>
  );
}


function ProductsAdmin() {
  const empty = { name: "", category: "General", description: "", link: "", image_url: "" };
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p.id); setForm({ name: p.name, category: p.category, description: p.description || "", link: p.link || "", image_url: p.image_url || "" }); setOpen(true); };

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try { const { url } = await uploadFile(file); setForm((f) => ({ ...f, image_url: url })); toast.success("Image uploaded"); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (editing) await api.put(`/products/${editing}`, form);
      else await api.post("/products", form);
      toast.success(editing ? "Product updated" : "Product added");
      setOpen(false); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name}?`)) return;
    try { await api.delete(`/products/${id}`); toast.success("Product removed"); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <p className="text-sm text-muted-foreground">Curate the recommended products shown to staff in Resources.</p>
        <div className="flex gap-2">
          <a href="https://www.kohc.co.uk/recommended-products" target="_blank" rel="noreferrer"><Button variant="outline" className="rounded-full">View website page</Button></a>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-product-btn" onClick={openNew} className="rounded-full bg-brand hover:bg-brand-hover text-white"><Plus className="h-4 w-4 mr-2" />Add product</Button></DialogTrigger>
            <DialogContent data-testid="product-dialog" className="max-h-[85vh] overflow-auto">
              <DialogHeader><DialogTitle>{editing ? "Edit product" : "Add product"}</DialogTitle></DialogHeader>
              <form onSubmit={save} className="space-y-4">
                <div><Label>Name</Label><Input data-testid="product-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Category</Label><Input data-testid="product-category" placeholder="e.g. Dry mouth, Brushes" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
                <div><Label>Description</Label><Input data-testid="product-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div><Label>Retailer link (optional)</Label><Input data-testid="product-link" placeholder="https://…" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} /></div>
                <div>
                  <Label>Product image</Label>
                  <div className="flex items-center gap-3">
                    <Input data-testid="product-image" type="file" accept="image/*" onChange={(e) => doUpload(e.target.files[0])} />
                    {uploading && <Loader2 className="h-4 w-4 animate-spin text-brand" />}
                    {form.image_url && <img src={form.image_url} alt="" className="h-10 w-10 object-contain rounded" />}
                  </div>
                  <Input className="mt-2" placeholder="…or paste an image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
                </div>
                <DialogFooter><Button data-testid="product-submit" type="submit" disabled={busy} className="rounded-full bg-coral text-white">{editing ? "Save changes" : "Add product"}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {products.map((p) => (
          <div key={p.id} data-testid={`admin-product-${p.id}`} className="bg-cream-card border border-cream-deep rounded-xl overflow-hidden flex flex-col">
            <div className="h-32 bg-white grid place-items-center p-3 border-b border-cream-deep">
              {p.image_url ? <img src={p.image_url} alt={p.name} className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-muted-foreground">No image</span>}
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <Badge variant="outline" className="w-fit">{p.category}</Badge>
              <h4 className="font-heading font-bold text-brand mt-1">{p.name}</h4>
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2 flex-1">{p.description}</p>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="rounded-full h-8 flex-1">Edit</Button>
                <Button size="sm" variant="ghost" data-testid={`del-product-${p.id}`} onClick={() => remove(p.id, p.name)} className="rounded-full h-8 text-rag-red hover:bg-rag-red-bg"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

